'use client';

import { Suspense, type ReactNode } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { ShieldCheck, Truck } from 'lucide-react';
import { FacebookIcon, GoogleIcon } from '@/components/icons/brand-icons';
import { ThemeToggle } from '@/components/theme/theme-toggle';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

function ProviderButton({
  provider,
  label,
  next,
  icon,
}: {
  provider: string;
  label: string;
  next: string;
  icon: ReactNode;
}) {
  return (
    <Button
      variant="outline"
      className="h-11 w-full justify-center gap-3 text-sm font-bold"
      render={<a href={`/auth/sign-in?provider=${provider}&next=${encodeURIComponent(next)}`} />}
    >
      {icon}
      {label}
    </Button>
  );
}

function LoginContent() {
  const params = useSearchParams();
  const next = params.get('next') ?? '/';
  const error = params.get('error');

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-background px-4 py-10" dir="rtl">
      <div className="absolute top-4 left-4">
        <ThemeToggle />
      </div>

      <Card className="w-full max-w-sm">
        <CardContent className="space-y-6">
          <div className="text-center">
            <h1 className="font-display text-2xl font-black">تسجيل الدخول</h1>
            <p className="mt-2 text-xs text-muted-foreground">
              سجّل الدخول لإتمام طلبك. الدفع عند الاستلام كما هو.
            </p>
          </div>

          {error && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-xs font-bold text-destructive">
              تعذّر تسجيل الدخول، يرجى المحاولة مجدداً.
            </div>
          )}

          <div className="space-y-3">
            <ProviderButton
              provider="google"
              label="المتابعة عبر Google"
              next={next}
              icon={<GoogleIcon className="h-5 w-5" />}
            />
            <ProviderButton
              provider="facebook"
              label="المتابعة عبر Facebook"
              next={next}
              icon={<FacebookIcon className="h-5 w-5 text-[#1877F2]" />}
            />
          </div>

          <div className="space-y-2 border-t border-border pt-5 text-start">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span>بياناتك محمية، لا نطلب أي كلمة مرور.</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Truck className="h-4 w-4 text-primary" />
              <span>توصيل لجميع الولايات مع الدفع عند الاستلام.</span>
            </div>
          </div>

          <Link href="/" className="block text-center text-xs font-bold text-muted-foreground hover:text-foreground">
            العودة إلى المتجر
          </Link>
        </CardContent>
      </Card>
    </main>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<main className="min-h-screen bg-background" />}>
      <LoginContent />
    </Suspense>
  );
}
